"use client";

import { useVisitorAge } from "@/hooks/use-visitor-age";
import { useAuth } from "@/lib/auth-context";
import {
  FOOTER_GRADE_PAGE_SLUGS,
  getFooterGradePageHref,
  getFooterGradePageLabel,
} from "@/lib/constants/footer-grade-links";
import Image from "next/image";
import Link from "next/link";

const FOOTER_LINK_CLASS = "text-gray-300 hover:text-white";

export function Footer() {
  // Use auth context instead of creating separate Supabase client
  // TEMPORARY: Blog link is hidden from non-authenticated users
  // TODO: To re-enable blog link for all users, remove this check and uncomment the blog link below
  const { user, isLoading } = useAuth();
  const { isKnownChild } = useVisitorAge();

  return (
    <footer className="bg-ink text-white py-8">
      <div className="mx-auto px-3 sm:px-4 max-w-[1024px]">
        <div className="flex flex-col md:flex-row justify-between items-center md:items-start mb-8">
          {/* Logo */}
          <div className="mb-8 md:mb-0">
            <Link href="/" className="flex items-center">
              <Image
                src="/images/logos/logo-white.svg"
                alt="PlayMath.org Logo"
                width={200}
                height={60}
                className="h-6 w-auto"
              />
            </Link>
          </div>

          {/* Links */}
          <div
            className="flex flex-col md:flex-row gap-8 md:gap-12 text-center md:text-left"
            data-testid="footer-link-columns"
          >
            <div>
              <h3 className="font-bold text-lg mb-4">Site Map</h3>
              <ul className="space-y-2">
                <li>
                  <Link href="/about" className={FOOTER_LINK_CLASS}>
                    About
                  </Link>
                </li>
                <li>
                  <Link href="/partners" className={FOOTER_LINK_CLASS}>
                    Partners
                  </Link>
                </li>
                <li>
                  <Link href="/educators" className={FOOTER_LINK_CLASS}>
                    Educators
                  </Link>
                </li>
                {!isKnownChild && (
                  <li>
                    <Link href="/contact" className={FOOTER_LINK_CLASS}>
                      Contact Us
                    </Link>
                  </li>
                )}
                <li>
                  <Link href="/brand" className={FOOTER_LINK_CLASS}>
                    Brand
                  </Link>
                </li>
                {/* TEMPORARY: Blog link hidden from non-authenticated users */}
                {/* TODO: To re-enable blog link for all users, remove this check and uncomment the blog link below */}
                {!isLoading && user && (
                  <li>
                    <Link href="/blog" className={FOOTER_LINK_CLASS}>
                      Blog
                    </Link>
                  </li>
                )}
                <li>
                  <Link href="/terms" className={FOOTER_LINK_CLASS}>
                    Terms of Service
                  </Link>
                </li>
                <li>
                  <Link href="/privacy" className={FOOTER_LINK_CLASS}>
                    Privacy Policy
                  </Link>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="font-bold text-lg mb-4">Grades</h3>
              <ul className="space-y-2">
                {FOOTER_GRADE_PAGE_SLUGS.map((slug) => (
                  <li key={slug}>
                    <Link href={getFooterGradePageHref(slug)} className={FOOTER_LINK_CLASS}>
                      {getFooterGradePageLabel(slug)}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Divider */}
      <div className="border-t border-brand-indigo-muted/25 my-6" />

      {/* Copyright */}
      <div className="mx-auto px-3 sm:px-4 max-w-[1024px]">
        <div className="text-center text-gray-400 text-sm">
          Copyright © {new Date().getFullYear()} PlayMath.org
          <br />
          <Link
            href="https://thinkinggames.com"
            className="text-gray-400 hover:text-white underline"
            target="_blank"
            rel="noopener noreferrer"
          >
            A Carina Thinking Games Initiative
          </Link>
        </div>
      </div>
    </footer>
  );
}
